import React, { useState } from 'react';
import CustomSelect from './CustomSelect';

function IssueDetail({
  issue,
  statuses,
  priorities,
  trackers = [],
  users = [],
  onUpdate,
  onClose,
  getStatusColor,
  getPriorityIcon
}) {
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Handle reply submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!notes.trim() && !newStatus) {
      setError('Please enter a note or change the status');
      return;
    }

    setSubmitting(true);
    setError(null);

    const updateData = {};
    if (notes.trim()) updateData.notes = notes;
    if (newStatus) updateData.status_id = newStatus;

    const result = await onUpdate(issue.id, updateData);

    if (result.success) {
      setNotes('');
      setNewStatus('');
    } else {
      setError(result.error);
    }

    setSubmitting(false);
  };

  // Map field names to friendly labels
  const fieldLabels = {
    'status_id': 'Status',
    'assigned_to_id': 'Assigned to',
    'priority_id': 'Priority',
    'tracker_id': 'Tracker',
    'subject': 'Subject',
    'description': 'Description',
    'done_ratio': 'Progress',
    'estimated_hours': 'Estimated time',
    'due_date': 'Due date',
    'start_date': 'Start date',
    'category_id': 'Category',
    'fixed_version_id': 'Target version',
    'parent_id': 'Parent task',
    'project_id': 'Project'
  };

  // Format field value - use name if available, otherwise show value
  const formatFieldValue = (name, value, details) => {
    if (!value && value !== 0) return '(none)';

    // Look up names for _id fields
    if (name === 'status_id') {
      const status = statuses.find(s => s.id === parseInt(value));
      return status?.name || value;
    }
    if (name === 'priority_id') {
      const priority = priorities.find(p => p.id === parseInt(value));
      return priority?.name || value;
    }
    if (name === 'tracker_id') {
      const tracker = trackers.find(t => t.id === parseInt(value));
      return tracker?.name || value;
    }
    if (name === 'assigned_to_id') {
      const user = users.find(u => u.id === parseInt(value));
      return user?.name || value;
    }
    if (name === 'done_ratio') {
      return `${value}%`;
    }

    return value;
  };

  // Extract property changes from journal details
  const getPropertyChanges = (details) => {
    if (!details || details.length === 0) return null;

    return details.map((d, i) => {
      if (d.property === 'attr') {
        const label = fieldLabels[d.name] || d.name.replace(/_/g, ' ').replace(/\bid\b/gi, '').trim();
        const oldVal = formatFieldValue(d.name, d.old_value, d);
        const newVal = formatFieldValue(d.name, d.new_value, d);

        return (
          <div key={i} className="property-change">
            <span className="prop-name">{label}:</span>
            <span className="prop-old">{oldVal}</span>
            <span className="prop-arrow">→</span>
            <span className="prop-new">{newVal}</span>
          </div>
        );
      }
      return null;
    }).filter(Boolean);
  };

  const priority = getPriorityIcon(issue.priority?.name);

  return (
    <div className="issue-detail">
      {/* Header */}
      <div className="detail-header">
        <button className="btn-close" onClick={onClose}>&times;</button>
        <div className="detail-title">
          <span className="detail-id">#{issue.id}</span>
          <h2>{issue.subject}</h2>
        </div>
        <div className="detail-badges">
          <span
            className="status-badge"
            style={{ backgroundColor: getStatusColor(issue.status?.name) }}
          >
            {issue.status?.name}
          </span>
          <span className="tracker-badge">{issue.tracker?.name}</span>
          {priority.icon && (
            <span className="priority-badge" style={{ color: priority.color }}>
              {priority.icon} {issue.priority?.name}
            </span>
          )}
        </div>
      </div>

      {/* Info bar */}
      <div className="detail-info">
        <div className="info-item">
          <label>Project:</label>
          <span>{issue.project?.name}</span>
        </div>
        <div className="info-item">
          <label>Author:</label>
          <span>{issue.author?.name}</span>
        </div>
        <div className="info-item">
          <label>Assignee:</label>
          <span>{issue.assigned_to?.name || 'Unassigned'}</span>
        </div>
        <div className="info-item">
          <label>Created:</label>
          <span>{formatDate(issue.created_on)}</span>
        </div>
        <div className="info-item">
          <label>Updated:</label>
          <span>{formatDate(issue.updated_on)}</span>
        </div>
      </div>

      {/* Description */}
      {issue.description && (
        <div className="detail-description">
          <h3>Description</h3>
          <div className="description-content">
            {issue.description.split('\n').map((line, i) => (
              <p key={i}>{line || '\u00A0'}</p>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      {issue.attachments && issue.attachments.length > 0 && (
        <div className="detail-attachments">
          <h3>Attachments ({issue.attachments.length})</h3>
          <div className="attachments-list">
            {issue.attachments.map(att => (
              <a
                key={att.id}
                href={`/api/redmine/attachments/download/${att.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="attachment-item"
              >
                <span className="att-icon">
                  {att.content_type?.startsWith('image/') ? '\uD83D\uDDBC' : '\uD83D\uDCC4'}
                </span>
                <span className="att-name">{att.filename}</span>
                <span className="att-size">{formatSize(att.filesize)}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Watchers */}
      {issue.watchers && issue.watchers.length > 0 && (
        <div className="detail-watchers">
          <h3>Watchers ({issue.watchers.length})</h3>
          <div className="watchers-list">
            {issue.watchers.map(w => (
              <span key={w.id} className="watcher-tag">{w.name}</span>
            ))}
          </div>
        </div>
      )}

      {/* Journal / History */}
      <div className="detail-journals">
        <h3>History</h3>
        {(!issue.journals || issue.journals.length === 0) ? (
          <p className="no-journals">No activity yet</p>
        ) : (
          <div className="journals-list">
            {issue.journals.slice().reverse().map(journal => (
              <div key={journal.id} className="journal-item">
                <div className="journal-header">
                  <span className="journal-author">{journal.user?.name}</span>
                  <span className="journal-date">{formatDate(journal.created_on)}</span>
                </div>

                {/* Property changes */}
                {journal.details && journal.details.length > 0 && (
                  <div className="journal-changes">
                    {getPropertyChanges(journal.details)}
                  </div>
                )}

                {/* Notes */}
                {journal.notes && (
                  <div className="journal-notes">
                    {journal.notes.split('\n').map((line, i) => (
                      <p key={i}>{line || '\u00A0'}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply Form */}
      <div className="detail-reply">
        <h3>Add Reply</h3>
        <form onSubmit={handleSubmit}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add a note..."
            rows={4}
          />

          <div className="reply-options">
            <div className="reply-status">
              <label>Change Status:</label>
              <CustomSelect
                value={newStatus}
                onChange={setNewStatus}
                placeholder="No change"
                options={[
                  { value: '', label: 'No change' },
                  ...statuses.map(s => ({ value: String(s.id), label: s.name }))
                ]}
              />
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>

          {error && <p className="reply-error">{error}</p>}
        </form>
      </div>
    </div>
  );
}

export default IssueDetail;
